
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Users, Clock, MapPin, X, Phone } from "lucide-react";
import { format, startOfMonth, endOfMonth, addMonths, subMonths, getDaysInMonth } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { createApiRequest } from "@/utils/apiUtils";

interface Trip {
  id: string;
  pickup_at: string;
  community_event: {
    name: string;
  };
  passenger_count: number;
  booking_duration: string;
}

interface ScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ScheduleDialog = ({ open, onOpenChange }: ScheduleDialogProps) => {
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDates, setSelectedDates] = useState<number[]>([]);
  
  const startDate = startOfMonth(currentMonth);
  const endDate = endOfMonth(currentMonth);
  
  // Format dates for API
  const formatDateForApi = (date: Date) => {
    return format(date, "yyyy-MM-dd'T'HH:mm:ss.SSSSSSXXX");
  };
  
  // Fetch trips for current month
  const { data: scheduledTrips, isLoading } = useQuery({
    queryKey: ["scheduledTrips", formatDateForApi(startDate), formatDateForApi(endDate)],
    queryFn: async () => {
      const request = createApiRequest(
        `trips/?type=claimed&pickup_at__gte=${formatDateForApi(startDate)}&pickup_at__lte=${formatDateForApi(endDate)}`
      );
      const response = await fetch(request);
      
      if (!response.ok) {
        throw new Error("Failed to fetch scheduled trips");
      }
      
      return response.json();
    },
    enabled: open // Only fetch when dialog is open
  });
  
  // Process trip data to extract dates and create events list
  useEffect(() => {
    if (scheduledTrips?.results) {
      // Fix the TypeScript error by properly mapping the array and ensuring it contains numbers
      const dates = scheduledTrips.results.map((trip: Trip) => {
        const date = new Date(trip.pickup_at);
        return date.getDate();
      });
      
      // Use type assertion to ensure TypeScript knows this is a number array
      setSelectedDates([...new Set(dates)] as number[]); 
    }
  }, [scheduledTrips]);
  
  // Month navigation
  const handlePreviousMonth = () => {
    setCurrentMonth(prevMonth => subMonths(prevMonth, 1));
  };
  
  const handleNextMonth = () => {
    setCurrentMonth(prevMonth => addMonths(prevMonth, 1));
  };
  
  // Generate calendar grid
  const generateCalendarGrid = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const days = [];
    
    for (let i = 1; i <= daysInMonth; i++) {
      const isSelected = selectedDates.includes(i);
      days.push(
        <div 
          key={i}
          className={`flex items-center justify-center h-12 w-12 text-center ${
            isSelected ? 'bg-neon-green text-black rounded-md' : 'text-gray-700'
          }`}
        >
          {i}
        </div>
      );
    }
    
    return days;
  };
  
  // Get upcoming events from scheduled trips
  const upcomingEvents = scheduledTrips?.results?.filter((trip: Trip) => {
    const tripDate = new Date(trip.pickup_at);
    const today = new Date();
    return tripDate >= today;
  }).slice(0, 2) || [];
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] p-0 rounded-lg bg-white">
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">My Schedule</h2>
            <button 
              onClick={() => onOpenChange(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {format(currentMonth, "MMMM yyyy")}
              </h3>
              <div className="flex space-x-2">
                <button 
                  onClick={handlePreviousMonth}
                  className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  <ChevronLeft size={20} className="text-gray-700" />
                </button>
                <button 
                  onClick={handleNextMonth}
                  className="p-2 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  <ChevronRight size={20} className="text-gray-700" />
                </button>
              </div>
            </div>
            
            <div className="border rounded-lg p-4">
              <div className="grid grid-cols-7 gap-1 mb-2">
                <div className="text-center font-medium text-gray-800">Mo</div>
                <div className="text-center font-medium text-gray-800">Tu</div>
                <div className="text-center font-medium text-gray-800">We</div>
                <div className="text-center font-medium text-gray-800">Th</div>
                <div className="text-center font-medium text-gray-800">Fr</div>
                <div className="text-center font-medium text-gray-800">Sa</div>
                <div className="text-center font-medium text-gray-800">Su</div>
              </div>
              
              <div className="grid grid-cols-7 gap-1">
                {generateCalendarGrid()}
              </div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">Upcoming events</h3>
              <button className="px-4 py-2 bg-gray-100 rounded-lg text-sm text-gray-800">
                Date Range
              </button>
            </div>
            
            <div className="space-y-4">
              {isLoading ? (
                <div className="text-center py-4 text-gray-700">Loading events...</div>
              ) : upcomingEvents.length === 0 ? (
                <div className="text-center py-4 text-gray-700">No upcoming events</div>
              ) : (
                upcomingEvents.map((trip: Trip, index: number) => {
                  const tripDate = new Date(trip.pickup_at);
                  const day = tripDate.getDate();
                  const isMonday = tripDate.getDay() === 1;
                  const isSaturday = tripDate.getDay() === 6;
                  const dayLabel = isMonday ? 'MON' : isSaturday ? 'SAT' : format(tripDate, 'EEE').toUpperCase();
                  
                  // Format duration (e.g., "04:00:00" => "4 hours")
                  const formatDuration = (duration: string): string => {
                    const match = duration.match(/^(\d+):/);
                    return match ? `${parseInt(match[1])} hours` : duration;
                  };
                  
                  return (
                    <div key={trip.id} className="border rounded-lg overflow-hidden">
                      <div className="flex">
                        <div className="bg-gray-100 p-4 flex flex-col items-center justify-center min-w-[80px]">
                          <div className="text-sm text-gray-600">{dayLabel}</div>
                          <div className="text-3xl font-bold text-gray-800">{day}</div>
                        </div>
                        
                        <div className="flex-1 p-4">
                          <h4 className="font-medium mb-2 text-gray-900">
                            {trip.community_event?.name || "Event Name"}
                          </h4>
                          
                          <div className="flex flex-wrap gap-6">
                            <div className="flex items-center gap-1 text-sm text-gray-700">
                              <Users size={16} />
                              <span>{trip.passenger_count || 12} guests</span>
                            </div>
                            
                            <div className="flex items-center gap-1 text-sm text-gray-700">
                              <Clock size={16} />
                              <span>{formatDuration(trip.booking_duration) || "3 hours"}</span>
                            </div>
                            
                            <div className="flex items-center gap-1 text-sm text-gray-700">
                              <MapPin size={16} />
                              <span>Route</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-neon-green p-4 flex flex-col items-center justify-center min-w-[80px]">
                          <Phone size={20} className="mb-1 text-black" />
                          <div className="text-xs text-black">CONTACT</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleDialog;
